// TOP LEVEL IMPORTS
import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import message from 'components/common/message';
import notification from 'antd/lib/notification';
import uuidv1 from 'uuid/v1';
// COMPONENTS
import ContractPageHeader from 'components/common/ContractPageHeader';
import ClientNameRow from './ClientNameRow';
import ContractStepHeader from './ContractStepHeader';
import CreateContractButton from './CreateContractButton';
import NextButton from './NextButton';
import BackButton from './BackButton';
import ClientInfo from './ClientInfo';
import PricingInfo from './PricingInfo';
import ContractTerms from './ContractTerms';
import InvoicesInfo from './InvoicesInfo';
// APOLLO
import { compose, graphql } from 'react-apollo';
import createInvoice from 'ApolloClient/Mutations/createInvoice';
import saveContract from 'ApolloClient/Mutations/saveContract';
import contractByIdQuery from 'ApolloClient/Queries/contractById';
// LIB
import RecomposeHelpers from 'lib/helpers/RecomposeHelpers';
import InvoiceHelpers from 'lib/helpers/InvoiceHelpers';
import ErrorHelpers from 'lib/helpers/ErrorHelpers';
import CurrencyHelpers from 'lib/helpers/CurrencyHelpers';
import { DEFAULT_CONTRACT_STATE } from './constants';
import contractFragment from '../../ApolloClient/Fragments/contractFragment';

// STYLED COMPONENTS
// ========================================
const StepHeader = styled.div`
  flex: 1;
  width: 750px;
  max-width: 100%;
`;

const LinkButton = styled.button`
  border: 0px;
  background: transparent;
  outline: 0;
  display: inline;
  color: ${p => p.theme.color.primary};
  padding: 0px;
  cursor: pointer;
`;

const StepsContainer = styled.div`
  flex: 1;
  min-height: 335px;
  width: 750px;
  padding: 5px;
  max-width: 100%;
`;

const ButtonContainer = styled.div`
  flex: 1;
  min-height: 100px;
  padding-top: 20px;
`;

// CONSTANTS
// ================================

class ContractDraft extends React.Component {
  state = {
    current: 0,
    hasError: false,
    step: 1,
    loading: false,
    contract: {
      ...DEFAULT_CONTRACT_STATE,
      salesTax:
        (this.props.contractByIdQuery.contractById && this.props.contractByIdQuery.contractById.salesTax)
        || (this.props.currentUser.organization && this.props.currentUser.organization.salesTax),
    },
  };

  componentDidMount() {
    // we want to update the local state with latest data from the server when component mounds
    this.onContractQueryComplete(this.props.contractByIdQuery.contractById);
  }

  getSalesTaxPercent = () => {
    let tax = 0;
    // local state takes precedence if it exists
    if (this.state.contract.salesTax) {
      tax = this.state.contract.salesTax;
    } else if (this.props.contractByIdQuery.contractById && this.props.contractByIdQuery.contractById.salesTax) {
      tax = this.props.contractByIdQuery.contractById.salesTax;
    } else if (this.props.currentUser.organization && this.props.currentUser.organization.salesTax) {
      tax = Number(this.props.currentUser.organization.salesTax);
    }
    return tax;
  };

  onContractQueryComplete = contractQueryResult => {
    console.log('onContractQueryComplete');
    // merge local state in with the latest database results
    let lineItems = this.formatLineItems(contractQueryResult.lineItems);
    console.log(lineItems);
    let newCalculation = this.reCalculateAmounts({
      ...this.state.contract, // local default state
      ...contractQueryResult, // remote data to merge locally
      ...lineItems,
      salesTax: this.getSalesTaxPercent(),
    });
    this.setState({
      contract: {
        ...this.state.contract, // local default state
        ...contractQueryResult, // remote data to merge locally
        ...lineItems,
        ...newCalculation,
      },
    });
  };

  onContractChange = newContractValue => {
    // recalculate everything based on the new invoice value
    let newCalculation = this.reCalculateAmounts({
      ...this.state.contract,
      ...newContractValue,
    });

    this.setState({
      contract: {
        ...this.state.contract,
        ...newContractValue,
        ...newCalculation,
      },
    });
  };

  // getDepositAmount = (
  //   depositValue,
  //   depositType,
  //   depositAmount,
  //   subtotalAfterTax,
  // ) => {

  //   if (depositValue === null) {
  //     depositValue = '0.00';
  //   }

  //   if (depositType === 'percentage') {
  //     let cleanDiscountValue = parseFloat(depositValue || 0, 10);
  //     depositAmount = subtotalAfterTax * (cleanDiscountValue / 100);
  //   }
  //   if (depositType === 'dollar-amount') {
  //     depositAmount = depositValue.replace('.', '');
  //   }

  //   return depositAmount;
  // };
  getDepositAmount = (depositValue = '0', depositType, depositAmount, subtotalAfterTax) => {
    if (depositValue === null) {
      depositValue = '0.00';
    }

    if (depositType === 'percentage') {
      let cleanDiscountValue = parseFloat(depositValue || 0, 10);
      depositAmount = subtotalAfterTax * (cleanDiscountValue / 100);
    }
    // if (depositType === 'dollar-amount') {
    //   depositAmount = depositValue.replace('.', '');
    // }

    if (depositType === 'dollar-amount') {
      depositAmount = (depositValue && depositValue.toString().replace('.', '')) || '0';
    }

    return depositAmount;
  };

  formatLineItems = lineItems => {
    // setup the default object that formatLineItems will return
    let lineItemsObj = {
      keys: [],
    };

    // if there are no line items, then we can just return an object with an empty keys array
    if (!lineItems || lineItems.length === 0) {
      return lineItemsObj;
    }

    // if line items exist, foreach over them and create a proprety for each one
    lineItems.forEach(({ amount, description }) => {
      let id = uuidv1();
      // this.setState({ keys: this.state.keys.push(id) })
      lineItemsObj[`names-${id}`] = { amount, description };
      lineItemsObj.keys.push(id);
    });

    return lineItemsObj;
  };

  reCalculateAmounts = newState => {
    try {
      let {
        keys,
        salesTax,
        discountValue,
        discountAmount,
        discountType,
        depositValue,
        depositType,
        depositAmount,
      } = newState;
      let subtotal = InvoiceHelpers.getSubTotal(keys, newState);
      let newDiscountAmount = InvoiceHelpers.getDiscountAmount(discountValue, discountType, discountAmount, subtotal);
      let subtotalAfterDiscount = subtotal - newDiscountAmount;
      let taxAmount = InvoiceHelpers.getTax(salesTax, subtotalAfterDiscount);
      let subtotalAfterTax = subtotalAfterDiscount + taxAmount;
      let newDepositAmount = this.getDepositAmount(depositValue, depositType, depositAmount, subtotalAfterTax);
      let finalPaymentAmount = subtotalAfterTax - newDepositAmount;
      let lineItems = InvoiceHelpers.getLineItems(newState);

      let newCalculations = {
        ...newState,
        depositAmount: Math.round(newDepositAmount),
        subtotal: Math.round(subtotal),
        subtotalAfterTax: Math.round(subtotalAfterTax),
        discountAmount: Math.round(newDiscountAmount),
        subtotalAfterDiscount: Math.round(subtotalAfterDiscount),
        finalPaymentAmount: Math.round(finalPaymentAmount),
        amount: Math.round(finalPaymentAmount),
        taxAmount: Math.round(taxAmount),
        lineItems,
      };

      return newCalculations;
    } catch (err) {
      console.log(err);
      return this.setState({ hasError: true });
    }
  };

  componentDidCatch(error, info) {
    // Display fallback UI
    this.setState({ hasError: true });
    // You can also log the error to an error reporting service

    // console.log({ error: error.message, info, raven: window.Raven });
  }

  renderStep = () => {
    let currencySymbol = CurrencyHelpers.getCurrencySymbol(this.props.contractByIdQuery.contractById.currency || 'USD');

    let defaultProps = {
      contract: this.state.contract,
      currentUser: this.props.currentUser,
      onChange: this.onContractChange,
      currencySymbol,
      keys: this.state.contract.keys,
      windowWidth: this.props.windowWidth,
    };

    // Return relevant form based on this.state.step

    if (this.state.step === 1) {
      return <ClientInfo {...defaultProps} />;
    }

    if (this.state.step === 2) {
      return <PricingInfo {...defaultProps} />;
    }

    if (this.state.step === 3) {
      return <ContractTerms {...defaultProps} />;
    }

    console.log('defaultProps');
    console.log(defaultProps);

    if (this.state.step === 4) {
      return <InvoicesInfo {...defaultProps} />;
    }

    return null;
  };

  updateAfterSaveMutation = (proxy, { data }) => {
    const { contractId } = this.props.match.params;
    let currentFragment = proxy.readFragment({
      fragment: contractFragment,
      fragmentName: 'contractFragment',
      id: contractId,
    });
    return console.log({
      currentFragment,
      data,
      combined: {
        ...currentFragment,
        ...data.saveContract,
      },
    });
    proxy.writeFragment({
      fragment: contractFragment,
      fragmentName: 'contractFragment',
      id: contractId,
      data: {
        ...currentFragment,
        ...data.saveContract,
      },
    });
  };

  onSave = async () => {
    this.setState({ loading: true });
    const { contractId } = this.props.match.params;
    const {
      contractType,
      currency,
      termsHTML,
      createInvoices,
      // discount
      discountType,
      discountValue,
      discountAmount,
      // final payment
      finalPaymentAmount,
      finalPaymentDueDate,
      salesTax,
      // deposit
      depositDueDate,
      depositType,
      depositAmount,
      depositValue,
      sessionDate,
      keys,
    } = this.state.contract;

    const variables = {
      _id: contractId,
      params: {
        contractType,
        currency,
        termsHTML,
        createInvoices,
        lineItems: keys.map
          ? keys.map(key => {
            let itemValue = this.state.contract[`names-${key}`];
            itemValue.amount = itemValue.amount.replace('.', '');
            return itemValue;
          })
          : [],
        // discount
        discountType,
        discountValue,
        discountAmount,
        // final payment
        finalPaymentAmount,
        finalPaymentDueDate,
        salesTax,
        // deposit
        depositDueDate,
        depositType,
        depositAmount,
        depositValue,
        sessionDate,
      },
    };

    try {
      await this.props.saveContract({ variables, update: this.updateAfterSaveMutation });
    } catch (err) {
      return ErrorHelpers.handleError(err);
    }
  };

  onClearState = () => {
    // do clearing of the state
  };

  createInvoices = async contractId => {
    const {
      depositAmount,
      depositDueDate,
      finalPaymentAmount,
      finalPaymentDueDate,
      clientId,
      taxAmount,
      createInvoices,
      keys,
      currency,
    } = this.state.contract;

    // check if user wants invoices created
    if (createInvoices !== 'create') {
      return null;
    }

    // create an array to hold the invoice objects we'll build below
    let invoices = [];

    // map the line items from redux to an array of objecs called lineItems
    let lineItems = keys.map(key => {
      let item = this.state.contract[`names-${key}`];
      item._id = uuidv1();
      return item;
    });

    // check if we should create a deposit invoice
    if (depositAmount) {
      let params = {
        contractId,
        currency,
        clientId,
        amount: depositAmount,
        finalPaymentAmount: depositAmount,
        due: depositDueDate || new Date(),
        lineItems: [],
        autosend: true,
      };
      // add deposit invoice to array of invoices to send
      invoices.push({ params });
    }

    // check if we should create a final payment invoice
    if (finalPaymentAmount) {
      let params = {
        contractId,
        clientId,
        currency,
        amount: finalPaymentAmount,
        depositAmount,
        finalPaymentAmount,
        due: finalPaymentDueDate || new Date(),
        lineItems,
        taxAmount,
        autosend: true,
      };

      // add finalPayment invoice to array of invoices to send
      invoices.push({ params });
    }
    console.log('invoices');
    console.log(invoices);
    // call apollo mutation for invoice 0
    if (invoices[0]) {
      console.log('invoices[0]');
      console.log(invoices[0]);
      try {
        await this.props.createInvoice({ variables: invoices[0] });
      } catch (err) {
        console.log(err);
        notification.error({
          message: 'there was a problem creating your invoices',
        });
      }
    }

    // call apollo mutation for invoice 1
    if (invoices[1]) {
      try {
        console.log('invoices[1]');
        console.log(invoices[1]);
        await this.props.createInvoice({ variables: invoices[1] });
      } catch (err) {
        console.log(err);
        notification.error({
          message: 'there was a problem creating your invoices',
        });
      }
    }

    this.setState({ loading: false });
    this.props.history.push(`/contract/sign/${contractId}?type=contractor`);
    window.location.reload();
  };

  onCreate = async () => {
    // grab props
    const { onClearState, history, match } = this.props;

    // the contract id is grabbed from the URL params
    const contractId = match.params.contractId;

    // set the loader so we disable the button, etc
    this.setState({ loading: true });

    // create variable so we can store the response
    let res;

    try {
      // call onSave which fires saveContract mutation
      await this.onSave();
    } catch (graphqlError) {
      ErrorHelpers.handelGraphqlError(graphqlError);
      return this.setState({ loading: false });
    }

    // check if we need to create invoices for this contract
    if (this.state.contract.createInvoices === 'create') {
      return this.createInvoices(contractId);
    }
    // turn off the loader
    this.setState({ loading: false });
    // clear the state
    this.onClearState();
    // push the user to the next page
    history.push(`/contract/sign/${contractId}?type=contractor`);
    // reload the app
    window.location.reload();
  };

  onNext = async () => {
    // whenever user goes to next page, save changes
    try {
      await this.onSave();
    } catch (err) {
      return console.log(err);
    }
    message.success('Progress saved!', 2);
    let currentStep = this.state.step;
    this.setState({ step: currentStep + 1, loading: false });
  };

  onBack = async () => {
    // whenever user goes to next page, save changes
    try {
      await this.onSave();
    } catch (err) {
      return console.log(err);
    }
    let currentStep = this.state.step;
    this.setState({ step: currentStep - 1, loading: false });
  };

  UNSAFE_componentWillMount() {
    this.setState({
      contract: {
        ...this.state.contract,
        ...this.props.contractByIdQuery.contractById,
      },
    });
  }

  renderBackButton = () => {
    if (this.state.step > 1) {
      return <BackButton onBack={this.onBack} />;
    }
  };

  renderButtons = () => {
    let disabled = false; // this.getDisabledState();

    if (this.state.step === 4) {
      return (
        <CreateContractButton
          onCreate={this.onCreate}
          createInvoices={this.state.contract.createInvoices === 'create'}
          loading={this.state.loading}
          renderBackButton={this.renderBackButton}
        />
      );
    }

    return (
      <NextButton
        onNext={this.onNext}
        loading={this.state.loading}
        renderBackButton={this.renderBackButton}
        disabled={disabled}
      />
    );
  };

  render() {
    const { contractById } = this.props.contractByIdQuery;

    // show an error if something goes wrong
    if (this.state.hasError) {
      return (
        <div>
          <ContractPageHeader
            currentUser={this.props.currentUser}
            current={this.state.current}
            contract={contractById}
          />
          <ClientNameRow client={contractById.client} />
          <div className="contract-step-container">
            <p>
              Oops Ned stumbled. Let's <LinkButton onClick={() => window.location.reload()}>refresh</LinkButton> and try
              one more time.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <ContractPageHeader currentUser={this.props.currentUser} current={this.state.current} contract={contractById} />
        <ClientNameRow client={contractById.client} />
        <div className="contract-step-container">
          <StepHeader>
            <ContractStepHeader step={this.state.step} windowWidth={this.props.windowWidth} />
          </StepHeader>
          <StepsContainer>{this.renderStep()}</StepsContainer>
          <ButtonContainer>{this.renderButtons()}</ButtonContainer>
        </div>
      </div>
    );
  }
}

let queryOptions = props => ({
  variables: { _id: props.match.params.contractId },
  fechPolicy: 'network-only',
});

let mutationOptions = props => ({
  variables: { _id: props.match.params.contractId },
});

export default compose(
  graphql(createInvoice, {
    options: mutationOptions,
    name: 'createInvoice',
  }),
  graphql(saveContract, {
    options: mutationOptions,
    name: 'saveContract',
  }),
  graphql(contractByIdQuery, { options: queryOptions, name: 'contractByIdQuery' }),
  RecomposeHelpers.errorHOC({ propName: 'contractByIdQuery' }),
  RecomposeHelpers.loadingHOC({ propName: 'contractByIdQuery' }),
)(ContractDraft);



// WEBPACK FOOTER //
// ./src/routes/contract-draft/index.js